import {mergeRegister} from '@lexical/utils';
import type {Binding} from '@lexical/yjs';
import type {LexicalEditor} from 'lexical';
import {
  BLUR_COMMAND,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CLEAR_HISTORY_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  PASTE_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import {useCallback, useEffect, useMemo} from 'react';
import {UndoManager} from 'yjs';

export function useYjsHistory(
  editor: LexicalEditor,
  binding: Binding,
  opts?: {captureTimeout?: number; wordBoundaries?: boolean}
): () => void {
  const captureTimeout = opts?.captureTimeout ?? 120;
  const wordMode = opts?.wordBoundaries ?? true;

  const undoManager = useMemo(
    () =>
      new UndoManager(binding.root.getSharedType(), {
        captureTimeout,
        trackedOrigins: new Set([binding]),
      }),
    [binding, captureTimeout]
  );

  // Override UNDO/REDO with higher priority so stock handler is ignored
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        UNDO_COMMAND,
        () => {
          undoManager.undo();
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        REDO_COMMAND,
        () => {
          undoManager.redo();
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        CLEAR_HISTORY_COMMAND,
        () => {
          undoManager.clear();
          editor.dispatchCommand(CAN_UNDO_COMMAND, false);
          editor.dispatchCommand(CAN_REDO_COMMAND, false);
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, undoManager]);

  // Natural boundaries
  useEffect(() => {
    if (!wordMode) return;
    const stop = () => queueMicrotask(() => undoManager.stopCapturing());
    const unEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      () => {
        stop();
        return false;
      },
      COMMAND_PRIORITY_EDITOR
    );
    const unPaste = editor.registerCommand(
      PASTE_COMMAND,
      () => {
        stop();
        return false;
      },
      COMMAND_PRIORITY_EDITOR
    );
    const unBlur = editor.registerCommand(
      BLUR_COMMAND,
      () => {
        stop();
        return false;
      },
      COMMAND_PRIORITY_EDITOR
    );
    return () => {
      unEnter();
      unPaste();
      unBlur();
    };
  }, [editor, undoManager, wordMode]);

  // Keep CAN_UNDO / CAN_REDO in sync
  useEffect(() => {
    const update = () => {
      editor.dispatchCommand(CAN_UNDO_COMMAND, undoManager.undoStack.length > 0);
      editor.dispatchCommand(CAN_REDO_COMMAND, undoManager.redoStack.length > 0);
    };
    undoManager.on('stack-item-added', update);
    undoManager.on('stack-item-popped', update);
    undoManager.on('stack-cleared', update);
    return () => {
      undoManager.off('stack-item-added', update);
      undoManager.off('stack-item-popped', update);
      undoManager.off('stack-cleared', update);
    };
  }, [editor, undoManager]);

  return useCallback(() => undoManager.clear(), [undoManager]);
}
